import { forwardRef, type ButtonHTMLAttributes, type KeyboardEvent, type MouseEvent } from 'react';

type ClickableProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> & {
  onClick: (e: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>) => void;
  /**
   * If true, render as an unstyled `<button>` (default). If you need a div for
   * complex layout reasons, use the `as="div"` variant — it adds role="button",
   * tabIndex, and keyboard handlers automatically.
   */
  as?: 'button' | 'div';
};

/**
 * Accessible clickable element. Defaults to `<button>` (the right answer 95% of
 * the time). The `as="div"` variant is for the rare layout-driven cases where
 * a button's intrinsic styling would interfere — it still adds keyboard support
 * and role="button" so assistive tech treats it correctly.
 *
 * Eliminates `jsx-a11y/no-static-element-interactions` and
 * `jsx-a11y/click-events-have-key-events` warnings categorically.
 *
 * Usage:
 *   <Clickable onClick={save} className="btn btn-primary">Save</Clickable>
 *   <Clickable as="div" onClick={openCard} className="card-row">Item</Clickable>
 */
const Clickable = forwardRef<HTMLButtonElement, ClickableProps>(function Clickable(
  { onClick, as = 'button', children, type, ...rest },
  ref,
) {
  if (as === 'div') {
    const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick(e);
      }
    };
    // Cast: rendered as div but typed as button for prop compatibility.
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onClick as (e: MouseEvent) => void}
        onKeyDown={onKeyDown as (e: KeyboardEvent) => void}
        {...(rest as object)}
      >
        {children}
      </div>
    );
  }

  return (
    <button ref={ref} type={type ?? 'button'} onClick={onClick} {...rest}>
      {children}
    </button>
  );
});

export default Clickable;
